/**
 * ADR-0202 — Guardrails "recent manifest changes" card.
 *
 * Renders the recent r18 skill-manifest observations for the active
 * site (from `GET /v1/sites/:site_id/manifest-observations`). The
 * observed digest is shown here — the surface is session-auth +
 * RLS-scoped, which is the load-bearing invariant that lets the ADR
 * §Decision 1 disclosure boundary hold (email/Slack digests carry NO
 * digest bytes). The card links the merchant to r18's rule editor to
 * approve the hash if the change is deliberate; it NEVER auto-edits
 * the policy — approval is a human action through the existing
 * policy-edit + publish flow (§Out of scope: no auto-approval).
 *
 * UX contract (each row has exactly two explicit choices — no bare X):
 *   - "Approve in r18" / "Add r18 rule" → parent opens rule editor
 *     with hash pre-seeded; auto-dismiss fires in onSaveRule.
 *   - "Dismiss" → parent marks the observation dismissed (API PATCH)
 *     and removes it optimistically. This is a deliberate "I've
 *     reviewed this and I'm not adding it to policy" action, not an
 *     accidental card-close.
 *
 * Render conditions (the parent supplies these via props):
 *   - `observations.length > 0` OR the site has r18 in its policy —
 *     otherwise the card is hidden entirely (the parent chooses).
 *   - CHANGE rows are surfaced at the top; first-sight rows are elided
 *     (the ingest pre-stamps them; they are baseline learning, not
 *     merchant-actionable).
 */

import { useMemo, useState } from 'react';
import { Clipboard, Check, AlertTriangle } from 'lucide-react';

import type { ManifestObservationItem } from '../../api/manifest-observations.js';
import { Card } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface ManifestChangeCardProps {
  readonly observations: readonly ManifestObservationItem[];
  /** True when r18 exists in the active policy (drives the CTA copy). */
  readonly r18_in_policy: boolean;
  /**
   * Called with a manifest_hash when the merchant clicks the approve CTA.
   * When r18 IS in the policy: parent scrolls to the r18 rule editor.
   * When r18 is NOT in the policy: parent opens the add-rule modal
   * pre-seeded with r18. NEVER auto-writes the policy.
   */
  readonly onApproveHash?: (hash: string) => void;
  /**
   * Called with an observation_id when the merchant clicks "Dismiss".
   * The parent removes the row optimistically and fires the API call in
   * the background. This is an explicit deliberate action (not an
   * accidental card-close) — the user is saying "I've reviewed this
   * and I'm not adding it to policy."
   */
  readonly onDismiss?: (observation_id: string) => void;
}

/**
 * Pure derivation: pick the change rows (first-sight rows are
 * baseline learning — the ingest pre-stamps them). Exported for the
 * unit tests + parent gating.
 */
export function selectChangeObservations(
  observations: readonly ManifestObservationItem[],
): readonly ManifestObservationItem[] {
  return observations.filter((o) => o.change);
}

/** ISO-8601 to a friendly relative string. Best-effort, no library. */
function relativeFrom(iso: string, now: Date = new Date()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const delta_s = Math.max(0, Math.floor((now.getTime() - t) / 1000));
  if (delta_s < 60) return 'just now';
  if (delta_s < 3600) return `${Math.floor(delta_s / 60)}m ago`;
  if (delta_s < 86400) return `${Math.floor(delta_s / 3600)}h ago`;
  return `${Math.floor(delta_s / 86400)}d ago`;
}

/** "1st", "2nd", "3rd", "4th", "5th" … */
function ordinal(n: number): string {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
}

export function ManifestChangeCard(props: ManifestChangeCardProps): JSX.Element | null {
  const C = useC();
  const changes = useMemo(
    () => selectChangeObservations(props.observations),
    [props.observations],
  );
  const [copiedHash, setCopiedHash] = useState<string | null>(null);
  if (changes.length === 0) return null;

  const copyToClipboard = async (hash: string): Promise<void> => {
    if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
      return;
    }
    try {
      await navigator.clipboard.writeText(hash);
      setCopiedHash(hash);
      setTimeout(() => setCopiedHash((prev) => (prev === hash ? null : prev)), 1200);
    } catch {
      /* clipboard unavailable — fail-silent */
    }
  };

  return (
    <Card className="mb-4" data-testid="guardrails-manifest-change-card">
      <div
        className="px-4 py-3 flex items-center gap-3"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        <AlertTriangle size={14} color={C.amberFg} className="flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1.5,
              color: C.amberFg,
              textTransform: 'uppercase',
            }}
          >
            MANIFEST CHANGE
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              marginTop: 2,
            }}
          >
            {changes.length === 1
              ? '1 previously unseen skill-manifest hash was observed'
              : `${changes.length} previously unseen skill-manifest hashes were observed`}
          </div>
        </div>
      </div>

      <div className="px-4 py-3">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.55,
            marginBottom: 12,
          }}
        >
          {props.r18_in_policy
            ? 'Add this hash to r18\'s approved list if the change is expected. If not, dismiss it — the agent will continue to be rejected by r18.'
            : 'r18 is not currently in your policy. "Add r18 rule" creates a manifest-integrity gate with this hash in the approved list.'}
        </div>

        <ul className="flex flex-col gap-3 list-none p-0 m-0">
          {changes.map((c) => (
            <li
              key={c.observation_id}
              className="p-3 flex flex-col gap-2"
              style={{
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                background: C.surface,
              }}
              data-testid={`manifest-change-row-${c.observation_id}`}
            >
              {/* Row 1 — agent + verdict + timestamp */}
              <div className="flex items-center gap-3 flex-wrap">
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.text,
                    fontWeight: 600,
                  }}
                >
                  {c.agent_id}
                </div>
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    letterSpacing: 1,
                    color: c.allowlisted ? C.muted : C.coral,
                    textTransform: 'uppercase',
                  }}
                >
                  {c.allowlisted ? 'passed r18' : 'rejected by r18'}
                </div>
                <div
                  className="ml-auto"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    color: C.muted,
                  }}
                >
                  {relativeFrom(c.first_seen_at)}
                </div>
              </div>

              {/* Row 2 — change count context */}
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 11,
                  color: C.muted,
                }}
              >
                {ordinal(c.change_count)} manifest change for this agent
              </div>

              {/* Row 3 — hash change: was → now */}
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                {c.previous_hash !== null && c.previous_hash !== c.manifest_hash ? (
                  <div style={{ color: C.muted }}>
                    was&nbsp;&nbsp;
                    <span title={c.previous_hash} /* title-ok: full value of the shortened hash */>
                      {c.previous_hash.slice(0, 16)}…
                    </span>
                  </div>
                ) : null}
                <div className="flex items-center gap-2">
                  <span style={{ color: C.muted }}>
                    {c.previous_hash !== null && c.previous_hash !== c.manifest_hash ? 'now' : 'hash'}
                    &nbsp;&nbsp;
                  </span>
                  <span
                    className="flex-1 min-w-0"
                    title={c.manifest_hash} /* title-ok: full value of the shortened hash */
                    style={{ color: C.text }}
                  >
                    {c.manifest_hash.slice(0, 16)}…
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      void copyToClipboard(c.manifest_hash);
                    }}
                    aria-label="Copy manifest hash"
                    className="p-1.5 flex-shrink-0"
                    style={{
                      background: 'transparent',
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      color: C.text,
                      cursor: 'pointer',
                    }}
                    data-testid={`manifest-change-copy-${c.observation_id}`}
                  >
                    {copiedHash === c.manifest_hash ? (
                      <Check size={12} />
                    ) : (
                      <Clipboard size={12} />
                    )}
                  </button>
                </div>
              </div>

              {/* Row 4 — explicit binary actions */}
              {(props.onDismiss !== undefined || props.onApproveHash !== undefined) ? (
                <div
                  className="flex items-center gap-2 pt-1"
                  style={{ borderTop: `1px solid ${C.border}`, marginTop: 4 }}
                >
                  {props.onDismiss !== undefined ? (
                    <button
                      type="button"
                      onClick={() => props.onDismiss?.(c.observation_id)}
                      aria-label="Dismiss this observation without approving"
                      className="px-2 py-1 flex-shrink-0"
                      style={{
                        background: 'transparent',
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        color: C.muted,
                        fontFamily: sansFont,
                        fontSize: 11,
                        cursor: 'pointer',
                      }}
                      data-testid={`manifest-change-dismiss-${c.observation_id}`}
                    >
                      Dismiss
                    </button>
                  ) : null}

                  {props.onApproveHash !== undefined ? (
                    <button
                      type="button"
                      onClick={() => props.onApproveHash?.(c.manifest_hash)}
                      className="ml-auto px-2 py-1 flex-shrink-0"
                      style={{
                        background: C.lime ?? '#D9FF3F',
                        border: 'none',
                        color: '#0d0d0d',
                        fontFamily: sansFont,
                        fontSize: 11,
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                      data-testid={`manifest-change-approve-${c.observation_id}`}
                    >
                      {props.r18_in_policy ? 'Approve in r18 →' : 'Add r18 rule →'}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}
